import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { callEdgeFunction } from "@/lib/edge-functions";
import { supabase } from "@/lib/supabase";

/**
 * Payroll data layer — Business Logic §11. Reads are RLS selects
 * (hr/admin/executive/superadmin; Hub Manager has NO payroll access,
 * SECURITY §2). Lines are written only by the nightly run (service_role);
 * draft → approved → paid transitions go through the payroll Edge Function,
 * matching its withAuth(["hr","admin","superadmin"]) gates.
 */

export function usePayrollRuns() {
  return useQuery({
    queryKey: ["payroll-runs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("payroll_runs")
        .select("id, period_start, period_end, status, created_at, approver:staff ( full_name )")
        .order("period_start", { ascending: false })
        .limit(60);
      if (error) throw error;
      return data;
    },
    staleTime: 60_000,
  });
}

export type PayrollRun = NonNullable<ReturnType<typeof usePayrollRuns>["data"]>[number];

export function usePayrollLines(runId: string | null) {
  return useQuery({
    queryKey: ["payroll-lines", runId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("payroll_lines")
        .select(
          "id, staff_id, sessions, commission, base_pay, bonus_pay, total, staff ( full_name, role )",
        )
        .eq("run_id", runId!)
        .order("total", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!runId,
    staleTime: 60_000,
  });
}

export type PayrollLine = NonNullable<ReturnType<typeof usePayrollLines>["data"]>[number];

function useInvalidatePayroll() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ["payroll-runs"] });
    queryClient.invalidateQueries({ queryKey: ["payroll-lines"] });
  };
}

/** draft → approved (HR/Admin authorize pay — Business Logic §11). */
export function useApprovePayrollRun() {
  const invalidate = useInvalidatePayroll();
  return useMutation({
    mutationFn: async (runId: string) => callEdgeFunction(`payroll/runs/${runId}/approve`),
    onSuccess: invalidate,
  });
}

/** approved → paid (after the actual disbursement happens outside the app). */
export function useMarkPayrollRunPaid() {
  const invalidate = useInvalidatePayroll();
  return useMutation({
    mutationFn: async (runId: string) => callEdgeFunction(`payroll/runs/${runId}/mark-paid`),
    onSuccess: invalidate,
  });
}

/** Client-side CSV of a run's lines (Phase Tracker asks for a spreadsheet export). */
export function linesToCsv(run: PayrollRun, lines: PayrollLine[]): string {
  const esc = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`;
  const rows = [
    `Payroll ${run.period_start} to ${run.period_end} (${run.status})`,
    "Staff,Role,Sessions,Commission,Base Pay,Bonus Pay,Total",
    ...lines.map((l) =>
      [
        esc(l.staff?.full_name ?? l.staff_id),
        esc(l.staff?.role ?? ""),
        l.sessions ?? 0,
        l.commission ?? 0,
        l.base_pay ?? 0,
        l.bonus_pay ?? 0,
        l.total,
      ].join(","),
    ),
  ];
  return rows.join("\n");
}
